// @ts-check

/**
 * @file Renders assigned users for a task.
 */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useLocalSearchParams} from "expo-router"
import {memo, useEffect} from "react"
import {View} from "react-native-propforge"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import Button from "../../../src/components/button"
import buildRoutePath from "../../../src/shared/build-route-path"
import Card from "../../../src/components/card"
import errorMessage from "../../../src/shared/error-message"
import {requiredRouteParam} from "../../../src/shared/route-params"
import ScreenTitle from "../../../src/components/screen-title"
import TaskSubnav from "../../../src/screens/tasks/subnav"
import {emptyTaskSubnavCounts, taskSubnavCountConfig, taskSubnavCountsFromTask} from "../../../src/screens/tasks/subnav-counts"
import {Task} from "../../../src/frontend-models/task"
import Text from "@kaspernj/api-maker/build/utils/text"
import useThemeColors, {useThemeName} from "../../../src/shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @typedef {import("../../../src/screens/tasks/subnav-counts").TaskSubnavCounts} TaskSubnavCounts */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} TasksAssignedUsersScreenProps */

/**
 * @typedef {object} TasksAssignedUsersScreenState
 * @property {{id: string, userId: string, userName: string}[]} assignments Loaded task assignments.
 * @property {boolean} loading Whether assignments are loading.
 * @property {{kind: "error", text: string} | null} status Screen status message.
 * @property {TaskSubnavCounts} subnavCounts Task subnav counts.
 * @property {Task | null} task Loaded task record.
 */

/**
 * Task assigned users list screen.
 * @augments {ShapeComponent<TasksAssignedUsersScreenProps, TasksAssignedUsersScreenState>}
 */
class TasksAssignedUsersScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {string} */
  taskID
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {TasksAssignedUsersScreenState} */
  state = {
    assignments:
      /** @type {{id: string, userId: string, userName: string}[]} */ [],
    loading: true,
    status: /** @type {{kind: "error", text: string} | null} */ null,
    subnavCounts: emptyTaskSubnavCounts(),
    task: /** @type {Task | null} */ null
  }

  /** Initializes route params and assignment loading. */
  setup() {
    useLocale()
    const params = useLocalSearchParams()

    const taskID = requiredRouteParam(params, "id", "Task ID")
    this.params = params
    this.taskID = taskID
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      let cancelled = false
      const loadData = async () => {
        try {
          const task = await Task
            .where({id: taskID})
            .preload({
              taskAssignedUsers: {user: true}
            })
            .withCount(taskSubnavCountConfig())
            .first()
          if (cancelled) return
          const taskAssignedUsers = task ? task.taskAssignedUsersLoaded() : []
          this.s.assignments = taskAssignedUsers.map((tau) => {
            const user = tau.user()
            const userName = user
              ? [user.firstName(), user.lastName()]
                  .filter(Boolean)
                  .join(" ")
                  .trim() ||
                user.email() ||
                String(user.id())
              : String(tau.userId())
            return {
              id: String(tau.id()),
              userId: String(tau.userId()),
              userName
            }
          })
          this.s.subnavCounts = task ? taskSubnavCountsFromTask(task) : emptyTaskSubnavCounts()
          this.s.task = task
          this.s.loading = false
        } catch (error) {
          if (cancelled) return
          this.s.loading = false
          this.s.status = {
            kind: "error",
            text: errorMessage(error)
          }
        }
      }
      void loadData()
      return () => {
        cancelled = true
      }
    }, [taskID])
  }

  /**
   * Renders assigned users for the task.
   * @returns {ReactNode} Assigned users screen content.
   */
  render() {
    const {assignments, loading, status, subnavCounts, task} = this.s
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    const projectID = task ? task.projectId() || "" : ""
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="tasksAssignedUsersScreen"
      >
        <ScreenTitle>{_("Assigned users")}</ScreenTitle>
        <View
          testID="tasksAssignedUsersScreen/content"
        >
          <View
            style={styles.headerRow ||= {
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "space-between",
              marginBottom: 16
            }}
            testID="tasksAssignedUsersScreen/headerRow"
          >
            <Text
              style={styles.title ||= {
                fontSize: 24,
                fontWeight: 700
              }}
              testID="tasksAssignedUsersScreen/title"
            >
              {_("Assigned users")}
            </Text>
            <Button
              href={buildRoutePath(
                this.params,
                projectID
                  ? `/tasks/${this.taskID}/edit?projectId=${encodeURIComponent(projectID)}`
                  : `/tasks/${this.taskID}/edit`
              )}
              iconName="pen-to-square"
              label={_("Edit")}
              testID="tasksAssignedUsersScreen/editButton"
            />
          </View>
          {task &&
            <TaskSubnav
              activeItem="assignedUsers"
              assignedUsersCount={subnavCounts.assignedUsersCount}
              commentsCount={subnavCounts.commentsCount}
              params={this.params}
              projectId={projectID}
              taskId={this.taskID}
              testID="tasksAssignedUsersScreen/subnav"
              timelogsCount={subnavCounts.timelogsCount}
            />
          }
          {status &&
            <Text
              style={styles[`statusError-${themeName}`] ||= {
                color: colors.dangerText,
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 16
              }}
              testID="tasksAssignedUsersScreen/status"
            >
              {status.text}
            </Text>
          }
          {loading &&
            <Text
              style={styles[`loadingText-${themeName}`] ||= {
                color: colors.textMuted,
                fontSize: 16
              }}
              testID="tasksAssignedUsersScreen/loadingText"
            >
              {_("Loading...")}
            </Text>
          }
          {!loading && assignments.length === 0 &&
            <Text
              style={styles[`emptyText-${themeName}`] ||= {
                color: colors.textMuted,
                fontSize: 16
              }}
              testID="tasksAssignedUsersScreen/emptyText"
            >
              {_("No assigned users")}
            </Text>
          }
          {!loading &&
            assignments.map((assignment) => (
              <Card
                key={assignment.id}
                style={styles.row ||= {marginBottom: 12}}
                testID={`tasksAssignedUsersScreen/row-${assignment.id}`}
              >
                <Text
                  style={styles.rowName ||= {
                    fontSize: 16,
                    fontWeight: 600
                  }}
                  testID={`tasksAssignedUsersScreen/row-${assignment.id}/name`}
                >
                  {assignment.userName}
                </Text>
              </Card>
            ))}
        </View>
      </View>
    )
  }

}

/** @type {NamedExoticComponent<TasksAssignedUsersScreenProps>} */
const TasksAssignedUsersScreenComponent = memo(shapeComponent(TasksAssignedUsersScreen))

export default TasksAssignedUsersScreenComponent
